import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import { ShieldCheck, User, RefreshCw, ChevronDown } from "lucide-react";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

interface ProtectionCard {
  icon: React.ElementType;
  title: string;
  desc: string;
}

const protections: ProtectionCard[] = [
  {
    icon: ShieldCheck,
    title: "Accidental damage",
    desc: "Covers accidental damage like drops, spills, and more.",
  },
  {
    icon: User,
    title: "On-Demand Support",
    desc: "Get expert support whenever you need help.",
  },
  {
    icon: RefreshCw,
    title: "Replacement",
    desc: "Quick replacement for your damaged or defective product.",
  },
];

type FaqItem = {
  q: string;
  a: string;
};

const FAQS: FaqItem[] = [
  {
    q: "What is a UrbanFlux Care Plan?",
    a: "UrbanFlux Care provides accidental damage coverage, extended warranty coverage and peace of mind. We get that accidents happen. That’s why with UrbanFlux Care, we’ll provide you with a replacement product should you need one. The enhanced coverage begins at the time of purchase, and covers until the end of the UrbanFlux Plan term length. See Terms and Conditions for additional details.",
  },
  {
    q: "Why should I add an The Urban-Flux Care Plan?",
    a: "The Urban-Flux provides coverage for accidental damage not covered by our manufacturer’s warranty starting on day one. After your manufacturer warranty expires, you’re still covered in case of Rips & Tears for the remainder of your Urban-Flux coverage term.",
  },
  {
    q: "What happens if I return my product?",
    a: "If you return your product, Urban Flux returns the amount you paid for your product protection. Depending on how much time has passed since your purchase, you may receive a prorated refund, less any paid service.",
  },
  {
    q: "I haven’t received a refund for my protection even though I returned my product.",
    a: "Urban-Flux handles all transactions for product protection sales and refunds. For assistance with refunds, contact our Customer Care.",
  },
  {
    q: "Is this plan the same as warranty or insurance?",
    a: "No. It is an optional protection plan for accidental events, separate from any manufacturer warranty or insurance.",
  },
];

const AccordionItem: React.FC<{
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (i: number) => void;
}> = ({ item, index, isOpen, onToggle }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<string>("0px");

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      setMaxH(`${el.scrollHeight}px`);
      const t = setTimeout(() => setMaxH("none"), 300);
      return () => clearTimeout(t);
    } else {
      if (el.scrollHeight && maxH === "none") {
        setMaxH(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setMaxH("0px"));
        });
      } else {
        setMaxH("0px");
      }
    }
  }, [isOpen]);

  return (
    <div className="tw-border-b tw-text-left tw-px-6 tw-border-gray-200">
      <button
        aria-expanded={isOpen}
        aria-controls={`faq-${index}`}
        onClick={() => onToggle(index)}
        className="tw-w-full tw-text-left tw-py-4 tw-flex tw-justify-between tw-items-start tw-gap-4 "
      >
        <span className="tw-text-lg tw-font-medium">{item.q}</span>
        <ChevronDown
          className={`tw-h-5 tw-w-5 tw-transition-transform tw-duration-300 ${
            isOpen ? "tw-rotate-180" : "tw-rotate-0"
          }`}
        />
      </button>

      <div
        id={`faq-${index}`}
        ref={contentRef}
        style={{
          maxHeight: maxH === "none" ? undefined : maxH,
          transition: "max-height 300ms ease, opacity 200ms ease",
          opacity: isOpen ? 1 : 0,
          overflow: "hidden",
        }}
        className="tw-pb-4 tw-pr-6 tw-text-sm tw-text-gray-700"
        aria-hidden={!isOpen}
      >
        <div className="tw-pt-1">{item.a}</div>
      </div>
    </div>
  );
};

const UrbanFluxCare: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleIndex = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-black tw-text-white tw-font-mori">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* HERO */}
        <section className="tw-relative tw-w-[95%] tw-mx-auto">
          <div className="tw-relative tw-overflow-hidden tw-flex tw-items-center tw-justify-start tw-rounded-xl tw-h-[80vh]">
            <img
              src="/clients/urbanflux/hero.webp"
              alt="Hero"
              className="tw-absolute tw-inset-0 tw-object-cover tw-w-full tw-h-full -tw-z-10"
            />
            <div className="tw-absolute tw-inset-0 tw-bg-black/40"></div>
            <div className="tw-relative tw-z-10 tw-max-w-xl tw-px-10 md:tw-px-24">
              <h1 className="tw-text-white tw-text-3xl md:tw-text-6xl tw-font-semibold">
                UrbanFlux Care Plan.
              </h1>
              <p className="tw-text-white tw-mt-4 md:tw-text-2xl">
                Peace of mind begins with the UrbanFlux Care Plan
              </p>
            </div>
          </div>
        </section>

        {/* 3 Features */}
        <section className="tw-bg-black tw-text-white tw-py-20 tw-text-center">
          <h2 className="tw-text-2xl md:tw-text-4xl tw-mb-16 tw-font-semibold">
            Enjoy peace of mind with the UrbanFlux Care Plan.
          </h2>
          <div className="tw-flex tw-flex-wrap tw-gap-16 tw-justify-center">
            {protections.map((p, i) => (
              <div
                key={i}
                className="tw-flex tw-flex-col tw-items-center tw-max-w-[280px]"
              >
                <div className="tw-bg-white/10 tw-rounded-full tw-p-6 tw-mb-6">
                  <p.icon className="tw-h-10 tw-w-10 tw-text-white" />
                </div>
                <h3 className="tw-text-xl tw-font-semibold">{p.title}</h3>
                <p className="tw-text-gray-400 tw-mt-3">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Depend section */}
        <section className="tw-bg-white tw-text-black tw-py-24">
          <div className="tw-w-[90%] tw-mx-auto tw-grid md:tw-grid-cols-2 tw-gap-16">
            <div className="tw-flex tw-flex-col tw-justify-center">
              <h2 className="tw-text-3xl md:tw-text-5xl tw-font-semibold tw-mb-8">
                You depend on your product, keep it safe for the long-haul.
              </h2>
              <p className="tw-text-gray-700 tw-text-lg">
                Peace of mind shouldn’t end when your warranty does. Thanks to
                the UrbanFlux Care Plan, it doesn’t have to. You’ll enjoy
                protection beyond your manufacturer’s warranty.
              </p>
            </div>
            <img
              src="/clients/urbanflux/depend.webp"
              alt="Depend"
              className="tw-w-full tw-h-[500px] tw-object-cover tw-rounded-2xl tw-shadow-2xl"
            />
          </div>
        </section>

        {/* Simple Coverage */}
        <section className="tw-py-24 tw-text-center tw-bg-gray-50 tw-text-black">
          <div className="tw-max-w-4xl tw-mx-auto tw-px-6">
            <h2 className="tw-text-3xl md:tw-text-5xl tw-font-semibold tw-mb-8">
              Protection made simple, coverage made strong
            </h2>
            <p className="tw-text-gray-700 tw-text-xl">
              ✓ Accidental Damage Coverage: from the date of plan purchase, it
              covers accidental damage from drops, spills, and pets.
            </p>
          </div>
        </section>

        <section className="tw-w-full tw-overflow-hidden">
          <img
            src="/clients/urbanflux/banner.webp"
            className="tw-w-full tw-h-[60vh] md:tw-h-[80vh] tw-object-cover tw-object-center"
            alt="UrbanFlux Banner"
          />
        </section>

        {/* File Claims */}
        <section className="tw-bg-white tw-text-black tw-py-24">
          <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-text-center">
            <h2 className="tw-text-3xl md:tw-text-5xl tw-font-semibold tw-mb-8">
              File claims, hassle–free.
            </h2>
            <p className="tw-text-gray-700 tw-text-xl tw-leading-relaxed">
              Filing a claim is quick and effortless. Just submit your request
              online, and our team will guide you through every step while
              keeping you updated until your issue is fully resolved.
            </p>
          </div>
        </section>

        {/* How to buy */}
        <section className="tw-bg-gray-50 tw-text-black tw-py-24 tw-border-y tw-border-gray-200">
          <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-text-center">
            <h2 className="tw-text-3xl md:tw-text-5xl tw-font-semibold tw-mb-8">
              How to buy the UrbanFlux Care Plan ?
            </h2>
            <p className="tw-text-gray-700 tw-text-xl">
              You can add The UrbanFlux Care to your cart when you buy the
              Urban–Flux product on{" "}
              <a
                href="https://urban-flux.com/"
                className="tw-text-blue-600 hover:tw-underline tw-font-medium"
              >
                https://urban-flux.com/
              </a>
              . Product owners who are eligible for The UrbanFlux Care Plan can
              find an offer in The Urban–Flux website.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="tw-bg-white tw-text-black tw-py-24">
          <div className="tw-w-[95%] tw-mx-auto tw-text-center">
            <h2 className="tw-text-3xl md:tw-text-5xl tw-font-semibold tw-mb-16">
              Frequently Asked Questions
            </h2>
            <div className="tw-max-w-4xl tw-mx-auto tw-mb-16 tw-shadow-2xl tw-border tw-border-gray-100 tw-rounded-2xl tw-overflow-hidden">
              {FAQS.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  item={item}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={toggleIndex}
                />
              ))}
            </div>
            <Link
              to={"/urbanFlux/tnc"}
              className="tw-text-xl tw-font-semibold tw-text-gray-600 hover:tw-text-black tw-transition-colors"
            >
              View Full Terms and Conditions
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default UrbanFluxCare;
